<template>
	<div class="transactions-table-filters">
		<section v-if="showClassificationFilters" class="filters">
			<h5>{{ t("filters.classification-title") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="assetTypes"
					:options="assetTypeSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.asset-type')"
					:placeholder="$t('filters.all-masculine')"
					:same-width="true"
				/>
			</div>

			<div class="content filters">
				<ArkListboxDropdown
					v-model="categories"
					:options="categoryOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="categoryLabel"
					:placeholder="$t('filters.all-feminine')"
					:searchNormalizer="normalizeLabel"
					:disabled="isCategoryDisabled"
					:same-width="true"
				/>
			</div>

			<div class="content filters">
				<ArkListboxDropdown
					v-model="subcategories"
					:options="subcategoryOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="subcategoryLabel"
					:placeholder="$t('filters.all-feminine')"
					:searchNormalizer="normalizeLabel"
					:disabled="isSubcategoryDisabled"
					:same-width="true"
				/>
			</div>

			<div class="content filters">
				<ArkListboxDropdown
					v-model="propertyUses"
					:options="propertyUseSelectOptions"
					:selectable-groups="true"
					:collapsible-groups="true"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.property-use')"
					:placeholder="$t('filters.all-feminine')"
					:searchNormalizer="normalizeLabel"
					:same-width="true"
				/>
			</div>
		</section>

		<section class="filters">
			<h5>{{ t("filters.price-title") }}</h5>
			<div class="content range-filters">
				<div class="range price">
					<div class="values">
						<RangeNotchedSlider
							v-model="priceRange"
							:label="`${$t('transactions-table.columns.purchase-price')} ($)`"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[250000, 1000000, 5000000]"
							unit-prefix="$"
						/>
					</div>
				</div>

				<div class="range building-price">
					<div class="values">
						<PricePerAreaRangeNotchedSlider
							v-model="pricePerBuildingSqftRange"
							:label="t('transaction.buildingMetrics.priceFilter')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[100, 250, 500]"
						/>
					</div>
				</div>

				<div class="range gross-annual-rent">
					<div class="values">
						<RangeNotchedSlider
							v-model="grossAnnualRentRange"
							:label="t('transactions-table.columns.gross-annual-rent')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[50000, 100000, 250000]"
							:precision="2"
							unit-prefix="$"
						/>
					</div>
				</div>

				<div class="range gross-monthly-rent-per-unit">
					<div class="values">
						<RangeNotchedSlider
							v-model="grossMonthlyRentPerUnitRange"
							:label="t('transactions-table.columns.gross-monthly-rent-per-unit')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[500, 1000, 2000]"
							:precision="2"
							unit-prefix="$"
						/>
					</div>
				</div>

				<div class="range gross-rent-multiplier">
					<div class="values">
						<RangeNotchedSlider
							v-model="grossRentMultiplierRange"
							:label="t('transactions-table.columns.gross-rent-multiplier')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[10, 15, 20]"
							:precision="2"
						/>
					</div>
				</div>

				<div class="range price-per-unit">
					<div class="values">
						<RangeNotchedSlider
							v-model="pricePerUnitRange"
							:label="$t('filters.price-per-unit')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[100000, 250000, 500000]"
							unit-prefix="$"
						/>
					</div>
				</div>
			</div>
		</section>

		<section class="filters">
			<h5>{{ t("filters.land-title") }}</h5>
			<div class="content range-filters">
				<div class="range superficy">
					<div class="values">
						<AreaRangeNotchedSlider
							v-model="superficyRange"
							:label="$t('opportunities.unitFields.superficy')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[10000, 50000, 250000]"
						/>
					</div>
				</div>

				<div class="range constructible">
					<div class="values">
						<AreaRangeNotchedSlider
							v-model="constructibleRange"
							:label="$t('opportunities.unitFields.constructible')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[15000, 50000, 200000]"
						/>
					</div>
				</div>
			</div>
		</section>

		<section class="filters">
			<h5>{{ t("filters.building-title") }}</h5>
			<div class="content range-filters">
				<div class="range building-area">
					<div class="values">
						<AreaRangeNotchedSlider
							v-model="buildingAreaRange"
							:label="$t('filters.building-area')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[5000, 25000, 100000]"
						/>
					</div>
				</div>

				<div class="range building-age">
					<div class="values">
						<RangeNotchedSlider
							v-model="buildingAgeRange"
							:label="$t('filters.building-age')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[5, 20, 50]"
							:open-ended-anchor="80"
						/>
					</div>
				</div>

				<div class="range residential-units">
					<div class="values">
						<RangeNotchedSlider
							v-model="residentialUnitsRange"
							:label="$t('opportunities.residentialUnits')"
							:min="residentialUnitsSliderMin"
							:max="residentialUnitsSliderMax"
							:thresholds="[15, 40, 100]"
						/>
					</div>
				</div>
			</div>
			<TransactionParkingFilters
				v-if="store.currentUser?.has(Permission.TransactionRead)"
				:parking-types="view.view.value.filter?.transaction?.parkingTypes"
				@update:parking-types="view.setTransactionParkingTypes($event)"
			/>
		</section>

		<section v-if="store.currentUser?.has(Permission.TransactionRead)" class="filters">
			<h5>{{ t("filters.transaction") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="transactionVerificationStatus"
					:options="transactionVerificationStatusSelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.transaction-verification-status-title')"
					:placeholder="$t('filters.all-masculine')"
					:same-width="true"
				/>
			</div>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="transactionBuyerStatus"
					:options="buyerStatusSelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.buyer-status-title')"
					:placeholder="$t('filters.all-masculine')"
					:same-width="true"
				/>
			</div>
			<div v-if="store.currentUser?.has(Permission.TransactionWrite)" class="content filters">
				<ArkListboxDropdown
					v-model="transactionSource"
					:options="transactionSourceSelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.transaction-source-title')"
					:placeholder="$t('filters.all-feminine')"
					:same-width="true"
				/>
			</div>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="transactionComposition"
					:options="transactionCompositionSelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.transaction-composition-title')"
					:placeholder="$t('filters.all-feminine')"
					:same-width="true"
				/>
			</div>
		</section>

		<section class="filters">
			<h5>{{ t("opportunities.locationAndType") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="regions"
					:options="regionSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.regions-title')"
					:placeholder="$t('opportunities.all-regions')"
					:searchNormalizer="normalizeLabel"
					:same-width="true"
				/>

				<ArkListboxDropdown
					v-model="boroughs"
					:options="boroughSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.boroughs-title')"
					:placeholder="$t('opportunities.all-boroughs')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableBoroughFilter"
					:same-width="true"
				/>
			</div>
		</section>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRaw, watch, nextTick } from "vue"
import { useI18n } from "vue-i18n"
import { config, Feature } from "@/AppConfig.ts"
import AreaRangeNotchedSlider from "@/components/ui/AreaRangeNotchedSlider.vue"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import TransactionParkingFilters from "./TransactionParkingFilters.vue"
import { RANGE_SLIDER_MAX, RESIDENTIAL_UNITS_SLIDER_MAX, RESIDENTIAL_UNITS_SLIDER_MIN } from "@/components/constants"
import RangeNotchedSlider from "@/components/ui/RangeNotchedSlider.vue"
import PricePerAreaRangeNotchedSlider from "@/components/ui/PricePerAreaRangeNotchedSlider.vue"
import { useView } from "@/composables/useView"
import { useTransactionClassificationFilter } from "@/composables/useTransactionClassificationFilter"
import { AssetType } from "@/graphql"
import {
	type BoroughRecord,
	type Municipality,
	TransactionComposition,
	TransactionMetadataBuyerStatus,
	TransactionSource,
} from "@/model/DataModel.ts"
import { Permission, UseRootStore } from "@/model/RootStore"
import { TransactionVerificationStatus, ViewScope } from "@/types"
import { propertyUseSelectOptions } from "@/utils/propertyUses"

const { t } = useI18n()
const store = UseRootStore()
const view = useView(ViewScope.TransactionsTable)

const assetTypes = ref<string[]>([])
const builtCategories = ref<string[]>([])
const builtSubcategories = ref<string[]>([])
const landCategories = ref<string[]>([])
const landSubcategories = ref<string[]>([])
const transactionVerificationStatus = ref<TransactionVerificationStatus | null>(null)
const transactionBuyerStatus = ref<string | null>(null)
const transactionSource = ref<string | null>(null)
const transactionComposition = ref<string | null>(null)
const superficyRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const constructibleRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const priceRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const pricePerBuildingSqftRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const grossAnnualRentRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const grossMonthlyRentPerUnitRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const grossRentMultiplierRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const residentialUnitsSliderMin = RESIDENTIAL_UNITS_SLIDER_MIN
const residentialUnitsSliderMax = RESIDENTIAL_UNITS_SLIDER_MAX
const residentialUnitsRange = ref<[number, number]>([residentialUnitsSliderMin, residentialUnitsSliderMax])
const pricePerUnitRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const buildingAreaRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const buildingAgeRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const propertyUses = ref<string[]>([])
const regions = ref<string[]>([])
const boroughs = ref<string[]>([])
let isApplyingViewFilter = false

const buyerStatusSelectOptions = computed(() =>
	Object.values(TransactionMetadataBuyerStatus).map(value => ({
		value,
		label: t(`filters.buyer-status-${value}`),
	}))
)

const transactionVerificationStatusSelectOptions = computed(() => [
	{ value: TransactionVerificationStatus.Verified, label: t("filters.transaction-verification-status-verified") },
	{ value: TransactionVerificationStatus.Unverified, label: t("filters.transaction-verification-status-unverified") },
])

const transactionSourceSelectOptions = computed(() =>
	(Object.values(TransactionSource) as string[]).map(value => ({
		value,
		label: t(`filters.transaction-source-${value}`),
	}))
)

const transactionCompositionSelectOptions = computed(() =>
	(Object.values(TransactionComposition) as string[]).map(value => ({
		value,
		label: t(`filters.transaction-composition-${value}`),
	}))
)

// regions / municipalities / boroughs -> multi (options come from real reference data, selection is local)
const regionSelectOptions = computed(() => regionItems.value.map(o => ({ value: o.id, label: o.label })))

const boroughSelectOptions = computed(() => boroughItems.value.map(o => ({ value: o.id, label: o.label })))

const selectedRegions = computed(() => new Set<string>(regions.value))

const shouldDisableBoroughFilter = computed(() => selectedRegions.value.size === 0)

const availableRegionNames = ref<string[]>([])
const availableMunicipalityRecords = ref<Municipality[]>([])
const availableBoroughRecords = ref<BoroughRecord[]>([])

// classification filters
const showClassificationFilters = computed(() => config.ui.has(Feature.DevTypes))
const assetTypeSelectOptions = computed(() => [
	...Object.values(AssetType).map(value => ({
		value,
		label: t(`property.assetType.${value}`),
	})),
	{ value: "none", label: t("property.assetType.none") },
])

const {
	categories,
	subcategories,
	categoryOptions,
	subcategoryOptions,
	categoryLabel,
	subcategoryLabel,
	isCategoryDisabled,
	isSubcategoryDisabled,
} = useTransactionClassificationFilter({
	assetTypes,
	landCategories,
	landSubcategories,
	builtCategories,
	builtSubcategories,
})

onMounted(async () => {
	const [regionNames, municipalityRecords, loadedBoroughs] = await Promise.all([
		store.getRegions,
		store.getMunicipalityRecords,
		store.getBoroughRecords,
	])
	availableRegionNames.value = regionNames
	availableMunicipalityRecords.value = municipalityRecords
	availableBoroughRecords.value = loadedBoroughs
})

// Normalize a label by removing accents and lowercasing it
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
function normalizeLabel(name: string) {
	return name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
}

const regionItems = computed(() =>
	toRaw(availableRegionNames.value).map(name => ({
		id: name,
		label: name,
	}))
)

const hasMunicipalityRegionData = computed(() =>
	availableMunicipalityRecords.value.some(municipality => municipality.regionName)
)

// municipality names within the selected regions, used to cascade the borough filter directly
// off regions now that the municipality filter itself lives in the toolbar quick filters
const municipalityNamesInSelectedRegions = computed(() => {
	const municipalityRecords = toRaw(availableMunicipalityRecords.value)
	const filteredMunicipalities = hasMunicipalityRegionData.value
		? municipalityRecords.filter(
				municipality => municipality.regionName && selectedRegions.value.has(municipality.regionName)
			)
		: municipalityRecords

	return new Set(filteredMunicipalities.map(municipality => municipality.name))
})

const boroughItems = computed(() =>
	toRaw(availableBoroughRecords.value)
		.filter(
			borough => borough.municipalityName && municipalityNamesInSelectedRegions.value.has(borough.municipalityName)
		)
		.map(borough => ({
			id: borough.name,
			label: borough.name,
		}))
		.sort((left, right) => left.label.localeCompare(right.label))
)

function filterValuesByOptions(values: string[], options: { value: string }[]): string[] {
	const validValues = new Set(options.map(option => option.value))
	return values.filter(value => validValues.has(value))
}

watch([regions, () => boroughSelectOptions.value.map(option => option.value)], () => {
	boroughs.value = shouldDisableBoroughFilter.value
		? []
		: filterValuesByOptions(boroughs.value, boroughSelectOptions.value)
})

function rangeValue(
	range: { min?: number | null; max?: number | null } | undefined,
	defaultRange: [number, number]
): [number, number] {
	return [range?.min ?? defaultRange[0], range?.max ?? defaultRange[1]]
}

function syncFilterValue<T>(source: { value: T }, update: (value: T) => void): void {
	watch(
		source,
		value => {
			if (isApplyingViewFilter) return
			update(value)
		},
		{ deep: true }
	)
}

syncFilterValue(assetTypes, view.setTransactionAssetTypes)
syncFilterValue(builtCategories, view.setTransactionBuiltCategories)
syncFilterValue(builtSubcategories, view.setTransactionBuiltSubcategories)
syncFilterValue(landCategories, view.setTransactionLandCategories)
syncFilterValue(landSubcategories, view.setTransactionLandSubcategories)
syncFilterValue(transactionVerificationStatus, view.setTransactionVerificationStatus)
syncFilterValue(transactionBuyerStatus, view.setTransactionBuyerStatus)
syncFilterValue(transactionSource, view.setTransactionSource)
syncFilterValue(transactionComposition, view.setTransactionComposition)
syncFilterValue(superficyRange, view.setTransactionLandAreaRange)
syncFilterValue(constructibleRange, view.setTransactionBuildableLandAreaRange)
syncFilterValue(priceRange, view.setTransactionPriceRange)
syncFilterValue(pricePerBuildingSqftRange, view.setTransactionPricePerBuildingSqftRange)
syncFilterValue(grossAnnualRentRange, view.setTransactionGrossAnnualRentRange)
syncFilterValue(grossMonthlyRentPerUnitRange, view.setTransactionGrossMonthlyRentPerUnitRange)
syncFilterValue(grossRentMultiplierRange, view.setTransactionGrossRentMultiplierRange)
syncFilterValue(residentialUnitsRange, view.setTransactionResidentialUnitsRange)
syncFilterValue(pricePerUnitRange, view.setTransactionPricePerUnitRange)
syncFilterValue(buildingAreaRange, view.setTransactionBuildingAreaRange)
syncFilterValue(buildingAgeRange, view.setTransactionBuildingAgeRange)
syncFilterValue(propertyUses, view.setTransactionPropertyUses)
syncFilterValue(regions, view.setTransactionRegions)
syncFilterValue(boroughs, view.setTransactionBoroughs)

watch(
	() => view.view.value.filter?.transaction,
	filter => {
		isApplyingViewFilter = true
		assetTypes.value = filter?.assetTypes ?? []
		builtCategories.value = filter?.builtCategories ?? []
		builtSubcategories.value = filter?.builtSubcategories ?? []
		landCategories.value = filter?.landCategories ?? []
		landSubcategories.value = filter?.landSubcategories ?? []
		transactionVerificationStatus.value = filter?.verificationStatus ?? null
		transactionBuyerStatus.value = filter?.buyerStatus ?? null
		transactionSource.value = filter?.source ?? null
		transactionComposition.value = filter?.composition ?? null
		superficyRange.value = rangeValue(filter?.landArea, [0, RANGE_SLIDER_MAX])
		constructibleRange.value = rangeValue(filter?.buildableLandArea, [0, RANGE_SLIDER_MAX])
		priceRange.value = rangeValue(filter?.price, [0, RANGE_SLIDER_MAX])
		pricePerBuildingSqftRange.value = rangeValue(filter?.pricePerBuildingSqft, [0, RANGE_SLIDER_MAX])
		grossAnnualRentRange.value = rangeValue(filter?.grossAnnualRent, [0, RANGE_SLIDER_MAX])
		grossMonthlyRentPerUnitRange.value = rangeValue(filter?.grossMonthlyRentPerUnit, [0, RANGE_SLIDER_MAX])
		grossRentMultiplierRange.value = rangeValue(filter?.grossRentMultiplier, [0, RANGE_SLIDER_MAX])
		residentialUnitsRange.value = rangeValue(filter?.residentialUnits, [
			residentialUnitsSliderMin,
			residentialUnitsSliderMax,
		])
		pricePerUnitRange.value = rangeValue(filter?.pricePerUnit, [0, RANGE_SLIDER_MAX])
		buildingAreaRange.value = rangeValue(filter?.buildingArea, [0, RANGE_SLIDER_MAX])
		buildingAgeRange.value = rangeValue(filter?.buildingAge, [0, RANGE_SLIDER_MAX])
		propertyUses.value = filter?.propertyUses ?? []
		regions.value = filter?.regions ?? []
		boroughs.value = filter?.boroughs ?? []
		nextTick(() => {
			isApplyingViewFilter = false
		})
	},
	{ deep: true, immediate: true }
)

const resetFilters = () => {
	assetTypes.value = []
	builtCategories.value = []
	builtSubcategories.value = []
	landCategories.value = []
	landSubcategories.value = []
	transactionVerificationStatus.value = null
	transactionBuyerStatus.value = null
	transactionSource.value = null
	transactionComposition.value = null
	superficyRange.value = [0, RANGE_SLIDER_MAX]
	constructibleRange.value = [0, RANGE_SLIDER_MAX]
	priceRange.value = [0, RANGE_SLIDER_MAX]
	pricePerBuildingSqftRange.value = [0, RANGE_SLIDER_MAX]
	grossAnnualRentRange.value = [0, RANGE_SLIDER_MAX]
	grossMonthlyRentPerUnitRange.value = [0, RANGE_SLIDER_MAX]
	grossRentMultiplierRange.value = [0, RANGE_SLIDER_MAX]
	residentialUnitsRange.value = [residentialUnitsSliderMin, residentialUnitsSliderMax]
	pricePerUnitRange.value = [0, RANGE_SLIDER_MAX]
	buildingAreaRange.value = [0, RANGE_SLIDER_MAX]
	buildingAgeRange.value = [0, RANGE_SLIDER_MAX]
	propertyUses.value = []
	regions.value = []
	boroughs.value = []
	view.resetFilter()
}

const activeFiltersCount = computed(() => {
	const isRangeActive = (range: [number, number], [min, max]: [number, number]) => range[0] !== min || range[1] !== max

	return [
		assetTypes.value.length > 0,
		builtCategories.value.length > 0,
		builtSubcategories.value.length > 0,
		landCategories.value.length > 0,
		landSubcategories.value.length > 0,
		transactionVerificationStatus.value != null,
		transactionBuyerStatus.value != null,
		transactionSource.value != null,
		transactionComposition.value != null,
		(view.view.value.filter?.transaction?.parkingTypes?.length ?? 0) > 0,
		isRangeActive(superficyRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(constructibleRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(priceRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(pricePerBuildingSqftRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(grossAnnualRentRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(grossMonthlyRentPerUnitRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(grossRentMultiplierRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(residentialUnitsRange.value, [residentialUnitsSliderMin, residentialUnitsSliderMax]),
		isRangeActive(pricePerUnitRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(buildingAreaRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(buildingAgeRange.value, [0, RANGE_SLIDER_MAX]),
		propertyUses.value.length > 0,
		regions.value.length > 0,
		boroughs.value.length > 0,
	].filter(Boolean).length
})

function hasActiveFilters(): boolean {
	return activeFiltersCount.value > 0
}

defineExpose({ resetFilters, hasActiveFilters, activeFiltersCount })
</script>

<style scoped>
.transactions-table-filters {
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);

	section {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		&:not(:last-child) {
			border-bottom: var(--border-width-thin) solid var(--neutral-200);
			padding-bottom: var(--spacing-lg);
		}
	}

	h5 {
		margin: 0 0 var(--spacing-xs);
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		text-transform: uppercase;
		letter-spacing: 0.02em;
	}

	.content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
	}

	.range-filters {
		padding: 0;

		.range {
			--input-width: var(--width-input-field);

			.values {
				margin-top: var(--spacing-sm);
			}
		}

		.date {
			padding-bottom: var(--spacing-md);
			border-bottom: var(--border-width-thin) solid var(--neutral-200);
		}

		&:not(:last-child) {
			margin-bottom: var(--spacing-sm);
		}

		&:last-child {
			.range:last-child :deep(.range-notched-slider) {
				padding-bottom: 0;
				border-bottom: none;
			}
		}
	}

	.filters {
		min-height: var(--spacing-2xl);
		font-size: var(--font-size-small);
		width: 100%;
		position: relative;
	}
}
</style>
